import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type IRadioButtonProps,
    RadioButton,
    Field
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "RadioButton";

const USAGE_CODE = `const [delivery, setDelivery] = useState("standard");

<View
    aria-label="Delivery method"
    role="radiogroup"
>
    <RadioButton
        onCheckedChange={() => setDelivery("standard")}
        subTitle="3–5 business days · Free"
        isChecked={delivery === "standard"}
        title="Standard delivery"
    />
    <RadioButton
        onCheckedChange={() => setDelivery("express")}
        isChecked={delivery === "express"}
        subTitle="1–2 business days"
        title="Express delivery"
    />
</View>`;

const GROUP_CODE = `const [payment, setPayment] = useState<string | null>(null);

<View
    aria-label="Payment method"
    aria-required={true}
    role="radiogroup"
>
    <RadioButton
        onCheckedChange={() => setPayment("card")}
        isChecked={payment === "card"}
        isError={payment === null}
        title="Credit card"
    />
    <RadioButton
        hintText={payment === null ? "Choose a payment method." : undefined}
        onCheckedChange={() => setPayment("transfer")}
        isChecked={payment === "transfer"}
        isError={payment === null}
        title="Bank transfer"
    />
</View>`;

const TYPES = [
    "primary",
    "neutral",
    "danger",
    "warning",
    "success",
    "info"
] as const;

const CARD_LOOK_CODE = `<RadioButton
    subTitle="Everyone can see your profile."
    onCheckedChange={() => setVisibility("public")}
    isChecked={visibility === "public"}
    title="Public"
    variant="card"
/>`;

const DELIVERIES = [
    {
        subTitleKey: "radioButtonPage.standardHint",
        titleKey: "radioButtonPage.standard",
        value: "standard"
    },
    {
        subTitleKey: "radioButtonPage.expressHint",
        titleKey: "radioButtonPage.express",
        value: "express"
    },
    {
        subTitleKey: "radioButtonPage.pickupHint",
        titleKey: "radioButtonPage.pickup",
        value: "pickup"
    }
] as const;

const PAYMENTS = [
    {
        titleKey: "radioButtonPage.card",
        value: "card"
    },
    {
        titleKey: "radioButtonPage.transfer",
        value: "transfer"
    },
    {
        titleKey: "radioButtonPage.cash",
        value: "cash"
    }
] as const;

const InteractiveRadioButton = ({
    isChecked: isInitiallyChecked = false,
    ...props
}: Omit<IRadioButtonProps, "onCheckedChange">) => {
    const [
        isChecked,
        setIsChecked
    ] = useState(isInitiallyChecked);

    return <RadioButton
        {...props}
        onCheckedChange={() => setIsChecked(true)}
        isChecked={isChecked}
    />;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("radioButtonPage.groupTitle"),
        id: "group"
    },
    {
        title: t("radioButtonPage.typesTitle"),
        id: "types"
    },
    {
        title: t("radioButtonPage.labelTitle"),
        id: "label"
    },
    {
        title: t("radioButtonPage.cardLookTitle"),
        id: "card-look"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const RadioButtonPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        delivery,
        setDelivery
    ] = useState<string>("standard");

    const [
        payment,
        setPayment
    ] = useState<string | null>(null);

    const [
        cardChoice,
        setCardChoice
    ] = useState<string>("public");

    const isPaymentMissing = payment === null;

    return <Article>
        <ComponentHeader
            lead={t("radioButtonPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("radioButtonPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("radioButtonPage.overviewCaption")}
                isStacked={true}
            >
                <div
                    aria-label={t("radioButtonPage.deliveryLabel")}
                    role="radiogroup"
                >
                    {
                        DELIVERIES.map((item) => <RadioButton
                            onCheckedChange={() => setDelivery(item.value)}
                            isChecked={delivery === item.value}
                            subTitle={t(item.subTitleKey)}
                            title={t(item.titleKey)}
                            key={item.value}
                        />)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("radioButtonPage.groupTitle")}
            id="group"
        >
            <Paragraph>
                {t("radioButtonPage.groupText")}
            </Paragraph>
            <Preview
                caption={t("radioButtonPage.groupCaption")}
                isStacked={true}
            >
                <div
                    aria-label={t("radioButtonPage.paymentLabel")}
                    aria-required={true}
                    role="radiogroup"
                >
                    {
                        PAYMENTS.map((item, index) => <RadioButton
                            hintText={isPaymentMissing && index === PAYMENTS.length - 1 ? t("radioButtonPage.paymentError") : undefined}
                            onCheckedChange={() => setPayment(item.value)}
                            isChecked={payment === item.value}
                            isError={isPaymentMissing}
                            title={t(item.titleKey)}
                            key={item.value}
                        />)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("radioButtonPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("radioButtonPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <InteractiveRadioButton
                        isChecked={true}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("radioButtonPage.labelTitle")}
            id="label"
        >
            <Paragraph>
                {t("radioButtonPage.labelText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <InteractiveRadioButton
                    subTitle={t("radioButtonPage.standardHint")}
                    title={t("radioButtonPage.standard")}
                    isChecked={true}
                />
                <InteractiveRadioButton
                    hintText={t("radioButtonPage.accountHint")}
                    title={t("radioButtonPage.account")}
                    isRequired={true}
                />
                <InteractiveRadioButton
                    title={t("radioButtonPage.gift")}
                    isOptional={true}
                />
                <InteractiveRadioButton
                    title={t("radioButtonPage.flip")}
                    spreadBehaviour="stretch"
                    isChecked={true}
                    isFlip={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("radioButtonPage.cardLookTitle")}
            id="card-look"
        >
            <Paragraph>
                {t("radioButtonPage.cardLookText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field.Group
                    aria-label={t("radioButtonPage.cardLookTitle")}
                    role="radiogroup"
                >
                    <RadioButton
                        onCheckedChange={() => setCardChoice("public")}
                        subTitle={t("radioButtonPage.cardLookSub")}
                        title={t("radioButtonPage.cardLookNew")}
                        isChecked={cardChoice === "public"}
                        variant="card"
                    />
                    <RadioButton
                        subTitle={t("radioButtonPage.cardLookTeamSub")}
                        onCheckedChange={() => setCardChoice("team")}
                        title={t("radioButtonPage.cardLookTeam")}
                        isChecked={cardChoice === "team"}
                        variant="card"
                    />
                </Field.Group>
            </Preview>
            <CodeBlock
                code={CARD_LOOK_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("radioButtonPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <InteractiveRadioButton
                    title={t("radioButtonPage.disabled")}
                    isDisabled={true}
                />
                <InteractiveRadioButton
                    title={t("radioButtonPage.disabledChecked")}
                    isDisabled={true}
                    isChecked={true}
                />
                <InteractiveRadioButton
                    title={t("radioButtonPage.loading")}
                    isLoading={true}
                />
                <InteractiveRadioButton
                    hintText={t("radioButtonPage.paymentError")}
                    title={t("radioButtonPage.card")}
                    isError={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("radioButtonPage.a11yRole"),
                    t("radioButtonPage.a11yKeyboard"),
                    t("radioButtonPage.a11yName"),
                    t("radioButtonPage.a11yRequired"),
                    t("radioButtonPage.a11yDisabled"),
                    t("radioButtonPage.a11yDisplayOnly")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("radioButtonPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("radioButtonPage.propIsChecked"),
                        defaultValue: "false",
                        name: "isChecked",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propIsDefaultChecked"),
                        name: "isDefaultChecked",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propOnCheckedChange"),
                        type: "(isChecked: boolean) => void",
                        name: "onCheckedChange"
                    },
                    {
                        description: t("radioButtonPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("radioButtonPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("radioButtonPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("radioButtonPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("radioButtonPage.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        description: t("radioButtonPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propIsError"),
                        defaultValue: "false",
                        name: "isError",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propIsOptional"),
                        defaultValue: "false",
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propOptionalText"),
                        name: "optionalText",
                        type: "string"
                    },
                    {
                        description: t("radioButtonPage.propIsFlip"),
                        defaultValue: "false",
                        name: "isFlip",
                        type: "boolean"
                    },
                    {
                        description: t("radioButtonPage.propSpreadBehaviour"),
                        defaultValue: "\"baseline\" · card: \"free\"",
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("radioButtonPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("radioButtonPage.propVariant"),
                        type: "\"plain\" | \"card\"",
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("radioButtonPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("radioButtonPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("radioButtonPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default RadioButtonPage;
